import { Box, Skeleton, Stack, useMediaQuery } from '@chakra-ui/react';
import type { SingleValue } from 'chakra-react-select';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type React from 'react';
import { createElement, useEffect, useMemo, useState } from 'react';
import { MdFullscreen, MdFullscreenExit, MdTableChart } from 'react-icons/md';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { DownloadIcon, HorizontalBarsIcon } from '@/assets/icons';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftText from '@/components/Atoms/FaceliftText/FaceliftText';
import FaceliftDatePicker, { type DateRange } from '@/components/Molecules/FaceliftDatePicker';
import { ProfileMenu } from '@/components/Molecules/ProfileMenu';
import Show from '@/components/Molecules/Show';
import type { TableData } from '@/components/Organisms/ChartWithTableView';
import ChartWithTableView from '@/components/Organisms/ChartWithTableView';
import { useColorMode } from '@/components/ui/color-mode-utils';
import { colors } from '@/components/ui/theme';
import { useGetCollectionsByPayorsAnalysis } from '@/hooks/service-hooks/collection';
import { useFullscreen } from '@/hooks/useFullscreen';
import { calculateChartDateRange, chartDateFilterOptions, DEFAULT_CHART_DATE_FILTER } from '@/utils/dateFilters';
import { generateCSVFromTable, generateExcelFromTable } from '@/utils/exportFiles';
import type { ProfileMenuItem } from '@/utils/profileMenuItems';
import type { PayorCollectionDonutChartProps } from './PayorCollectionDonutChart.types';

dayjs.extend(utc);
dayjs.extend(timezone);

// Color palette for chart segments - blue to teal gradient
const CHART_COLORS = [
	colors.chartGradient1,
	colors.chartGradient4, 
	colors.chartGradient2, 
	colors.chartGradient3, 
	colors.chartGradient5,
	colors.chartGradient6,
];

interface ChartDataItem {
	name: string;
	value: number;
	color: string;
}

interface TooltipPayloadItem {
	name: string;
	value: number;
	payload: ChartDataItem;
}

interface CustomTooltipProps {
	active?: boolean;
	payload?: TooltipPayloadItem[];
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
	if (!active || !payload || payload.length === 0) return null;

	const data = payload[0].payload;

	return (
		<Box
			p={3}
			borderRadius="md"
			borderWidth="1px"
			boxShadow="lg"
			_dark={{ bg: 'darkModeBgColor', borderColor: 'gray.600' }}
			_light={{ bg: 'white', borderColor: 'gray.200' }}
		>
			<FaceliftText
				fontSize="sm"
				fontWeight="semibold"
				mb={2}
				_dark={{ color: 'white' }}
				_light={{ color: 'gray.800' }}
			>
				{data.name}
			</FaceliftText>
			<Stack gap={1}>
				<Stack direction="row" align="center" gap={2}>
					<Box width={2} height={2} borderRadius="full" bg={data.color} />
					<FaceliftText fontSize="xs" _dark={{ color: 'gray.300' }} _light={{ color: 'gray.600' }}>
						${data.value?.toLocaleString()}
					</FaceliftText>
				</Stack>
			</Stack>
		</Box>
	);
};

const renderCustomLabel = (entry: any) => {
	const { cx, cy, midAngle, outerRadius, name, value, percent } = entry;
	const RADIAN = Math.PI / 180;
	const safeName = typeof name === 'string' && name.trim().length > 0 ? name : 'Series';
	
	// Only show labels for segments that are large enough (>2%)
	if (percent < 0.02) return null;
	
	// Point at outer edge of donut
	const radius1 = outerRadius + 8;
	const x1 = cx + radius1 * Math.cos(-midAngle * RADIAN);
	const y1 = cy + radius1 * Math.sin(-midAngle * RADIAN);
	
	// Extend line horizontally
	const radius2 = outerRadius + 18;
	const x2 = cx + radius2 * Math.cos(-midAngle * RADIAN);
	const y2 = cy + radius2 * Math.sin(-midAngle * RADIAN);
	
	// Text position
	const textAnchor = x2 > cx ? 'start' : 'end';
	const textX = x2 > cx ? x2 + 5 : x2 - 5;
	
	// Truncate long names
	const maxLength = 12;
	const displayName = safeName.length > maxLength ? `${safeName.substring(0, 12)}...` : safeName;
	
	return (
		<g>
			{/* Line from donut to label */}
			<path
				d={`M ${x1},${y1} L ${x2},${y2}`}
				stroke="currentColor"
				strokeWidth={0.8}
				fill="none"
				opacity={0.5}
			/>
			{/* Label text */}
			<text
				x={textX}
				y={y2}
				fill="currentColor"
				textAnchor={textAnchor}
				dominantBaseline="central"
				style={{ fontSize: '10px', fontWeight: 600 }}
			>
				<tspan x={textX} dy="-0.6em">{displayName}</tspan>
				<tspan x={textX} dy="1.4em" style={{ fontSize: '9px', fontWeight: 400 }}>
					${value?.toLocaleString()}
				</tspan>
			</text>
		</g>
	);
};

const PayorCollectionDonutChart: React.FC<PayorCollectionDonutChartProps> = ({
	title = '',
	data: propData,
	isLoading: propIsLoading = false,
	height = 500,
	disableScroll = false,
	showFilters = false,
	showTableMenu = false,
	clinicsProductsFilter = '61aea445-e3b4-ea11-a816-000d3a191041|;',
	...rest
}) => {
	const { colorMode } = useColorMode();
	const isDark = colorMode === 'dark';

	// State for menu and table modal
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isTableModalOpen, setIsTableModalOpen] = useState(false);
	
	// State for disabled legend items
	const [disabledSeries, setDisabledSeries] = useState<Set<string>>(new Set());
	
	// Fullscreen hook
	const { chartContainerRef, isFullScreen, handleFullScreen } = useFullscreen();

	// Detect screen sizes for chart positioning
	const [isAbove1020] = useMediaQuery(['(min-width: 1020px)']);
	const [isAbove1200] = useMediaQuery(['(min-width: 1200px)']);
	const [isScrollRange] = useMediaQuery(['(min-width: 1000px) and (max-width: 1200px)']);

	// Position chart to left only when scroll is active (between 1020px-1200px)
	const chartCenterX = useMemo(() => {
		if (isAbove1200) return '50%'; // >= 1200px: centered (no scroll)
		if (isAbove1020 && !isAbove1200) return '35%'; // 1020-1200px: left (scroll active)
		return '50%'; // < 1020px: centered (mobile)
	}, [isAbove1020, isAbove1200]);

	// State for selected date filter (default to Year to Date)
	const [selectedFilter, setSelectedFilter] = useState<SelectItem | null>(DEFAULT_CHART_DATE_FILTER);
	const [isDateFilterModalOpen, setIsDateFilterModalOpen] = useState<boolean>(false);
	const [customDateRange, setCustomDateRange] = useState<DateRange>({
		startDate: '',
		endDate: '',
	});

	// Calculate date range based on selected filter
	const [dateRange, setDateRange] = useState<{ start: string; end: string }>(
		selectedFilter ? calculateChartDateRange(selectedFilter.value as string) || { start: '', end: '' } : { start: '', end: '' }
	);

	// Fetch data from API when showFilters is true
	const { data: apiResponse, isFetching } = useGetCollectionsByPayorsAnalysis(
		{
			clinicsProductsFilter,
			multi_filter: '',
			date_filter_range: `${dateRange.start},${dateRange.end}`,
		},
		showFilters,
	);

	// Use API data if showFilters is true, otherwise use propData
	const rawData = (showFilters && apiResponse?.data ? apiResponse.data : propData) || [];
	const isLoading = showFilters ? isFetching : propIsLoading;

	// Transform data and assign colors
	const chartData = useMemo(() => {
		return rawData.map((item, index) => {
			const rawName = (item as any)?.name;
			const safeSeriesName =
				typeof rawName === 'string' && rawName.trim().length > 0 ? rawName : `Series ${index + 1}`;

			return {
				name: safeSeriesName,
				value: (item as any)?.y ?? 0,
				color: CHART_COLORS[index % CHART_COLORS.length],
			};
		});
	}, [rawData]);

	// Filter out disabled series
	const visibleChartData = useMemo(() => {
		return chartData.filter(item => !disabledSeries.has(item.name));
	}, [chartData, disabledSeries]);

	// Handle filter change
	const handleFilterChange = (newValue: SingleValue<SelectItem>) => {
		if (newValue) {
			setSelectedFilter(newValue);
			// If custom is selected, open the modal (useEffect will also handle this)
			if (newValue.value === 'custom') {
				setIsDateFilterModalOpen(true);
			}
		}
	};

	// Watch for selectedFilter changes to handle date range updates
	useEffect(() => {
		if (selectedFilter?.value === 'custom') {
			setIsDateFilterModalOpen(true);
		} else if (selectedFilter?.value) {
			setDateRange(calculateChartDateRange(selectedFilter.value as string) || { start: '', end: '' });
		}
	}, [selectedFilter]);

	// Handle legend click to toggle series visibility
	const handleLegendClick = (seriesName: string) => {
		setDisabledSeries((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(seriesName)) {
				newSet.delete(seriesName);
			} else {
				newSet.add(seriesName);
			}
			return newSet;
		});
	};

	// Export functions
	const exportAsCSV = () => {
		const columns = [
			{ key: 'name', label: 'Payor' },
			{ key: 'value', label: 'Collection Amount' },
		];
		generateCSVFromTable(columns, chartData, 'PayorCollectionAnalysis', true);
	};

	const exportAsExcel = () => {
		const columns = [
			{ key: 'name', label: 'Payor' },
			{ key: 'value', label: 'Collection Amount' },
		];
		generateExcelFromTable(columns, chartData, 'PayorCollectionAnalysis', true);
	};

	// Handle menu item click
	const handleMenuNavigate = (href?: string) => {
		if (href === '#view-table') {
			setIsTableModalOpen(true);
		} else if (href === '#view-fullscreen') {
			handleFullScreen();
		} else if (href === '#download-csv') {
			exportAsCSV();
		} else if (href === '#download-xlsx') {
			exportAsExcel();
		}
	};

	// Chart options menu items
	const chartMenuItems: ProfileMenuItem[] = useMemo(() => [
		{
			title: 'View Table',
			icon: createElement(MdTableChart, { width: 18, height: 18 }),
			href: '#view-table',
		},
		{
			title: 'View in full screen',
			icon: createElement(MdFullscreen, { width: 18, height: 18 }),
			href: '#view-fullscreen',
		},
		{
			title: 'Download as CSV',
			icon: createElement(DownloadIcon, { width: 18, height: 18 }),
			href: '#download-csv',
		},
		{
			title: 'Download as XLSX',
			icon: createElement(DownloadIcon, { width: 18, height: 18 }),
			href: '#download-xlsx',
		},
	], []);

	// Check if we have data
	const hasData = useMemo(() => {
		return visibleChartData && visibleChartData.length > 0;
	}, [visibleChartData]);

	// Prepare table data for ChartWithTableView
	const tableData: TableData = useMemo(() => ({
		columns: [
			{ key: 'name', label: 'Payor', align: 'left' as const },
			{ 
				key: 'value', 
				label: 'Collection Amount', 
				align: 'right' as const,
				render: (value: number) => `$${value.toLocaleString()}`
			},
		],
		rows: chartData,
	}), [chartData]);

	// Calculate chart height
	const chartHeight = isFullScreen ? window.innerHeight - 150 : height - 80;

	return (
		<ChartWithTableView 
			title={title} 
			tableData={tableData} 
			showMenu={false}
			isTableModalOpen={isTableModalOpen}
			setIsTableModalOpen={setIsTableModalOpen}
		>
			<Show when={isLoading}>
				<Box
					width="100%"
					height={`${height}px`}
					borderRadius="md"
					borderWidth="1px"
					borderColor={'gray.400'}
					p={3}
					pl={2}
					_dark={{ bg: 'darkModeBgColor' }}
					_light={{ bg: 'white' }}
					overflow="hidden"
					display="flex"
					flexDirection="column"
					{...rest}
				>
					{/* Title and Filter Skeleton */}
					<Box display="flex" justifyContent="space-between" alignItems="center" mb={4} mt={2} ml={4} mr={4}>
						<Skeleton
							height={5}
							width="200px"
							borderRadius="md"
							_dark={{ bg: 'gray.700' }}
							_light={{ bg: 'gray.200' }}
						/>
						<Skeleton
							height={8}
							width="130px"
							borderRadius="md"
							_dark={{ bg: 'gray.700' }}
							_light={{ bg: 'gray.200' }}
						/>
					</Box>

					{/* Chart Area */}
					<Box
						flex="1"
						display="flex"
						alignItems="center"
						justifyContent="center"
						px={6}
						pb={4}
					>
						<Box
							position="relative"
							width={{ base: '200px', sm: '220px', md: '240px' }}
							height={{ base: '200px', sm: '220px', md: '240px' }}
							display="flex"
							alignItems="center"
							justifyContent="center"
						>
							{/* Outer circle */}
							<Skeleton height="100%" width="100%" borderRadius="full" position="absolute" _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
							{/* Inner circle to create donut effect */}
							<Box
								height={{ base: '80px', sm: '88px', md: '96px' }}
								width={{ base: '80px', sm: '88px', md: '96px' }}
								borderRadius="full"
								position="absolute"
								_dark={{ bg: 'darkModeBgColor' }}
								_light={{ bg: 'white' }}
								zIndex={1}
							/>
						</Box>
					</Box>
				</Box>
			</Show>

			<Show when={!isLoading}>
				<Box
					ref={chartContainerRef}
					width="100%"
					height={isFullScreen ? '100vh' : `${height}px`}
					borderRadius="md"
					borderWidth="1px"
					borderColor={'gray.400'}
					p={isFullScreen ? 8 : 3}
					pl={isFullScreen ? 8 : 2}
					_dark={{ bg: 'darkModeBgColor' }}
					_light={{ bg: 'white' }}
					overflow="hidden"
					display="flex"
					flexDirection="column"
					position="relative"
					{...rest}
				>
					{/* Exit Fullscreen Button */}
					<Show when={isFullScreen}>
						<FaceliftIconButton
							position="absolute"
							top={4}
							right={4}
							zIndex={1000}
							aria-label="Exit fullscreen"
							onClick={handleFullScreen}
							size="md"
							variant="solid"
							_dark={{ bg: 'gray.700', _hover: { bg: 'gray.600' }, color: 'white' }}
							_light={{ bg: 'gray.100', _hover: { bg: 'gray.200' }, color: 'gray.800' }}
						>
							<MdFullscreenExit size={20} />
						</FaceliftIconButton>
					</Show>
					<Show when={!!title}>
						<Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={2} ml={4} mr={4}>
							<FaceliftText
								fontSize="md"
								fontWeight="semibold"
								_dark={{ color: 'white' }}
								_light={{ color: 'gray.800' }}
							>
								{title}
							</FaceliftText>
							<Show when={!isFullScreen}>
								<Stack direction="row" gap={2} align="center">
									<Show when={showFilters}>
										<Box width="130px">
											<FaceliftSelect<SelectItem, false>
												placeholder="Select Date Range"
												value={selectedFilter}
												onChange={handleFilterChange}
												items={chartDateFilterOptions}
												isSearchable={false}
											/>
										</Box>
									</Show>
									{/* Chart Options Menu */}
									<Show when={showTableMenu}>
										<ProfileMenu
											trigger={
												<FaceliftIconButton
													aria-label="chart options menu"
													size="sm"
													variant="ghost"
												>
													<HorizontalBarsIcon width={16} height={15} />
												</FaceliftIconButton>
											}
											isOpen={isMenuOpen}
											onOpenChange={setIsMenuOpen}
											onNavigate={handleMenuNavigate}
											menuItems={chartMenuItems}
										/>
									</Show>
								</Stack>
							</Show>
						</Box>
					</Show>
					<Show when={!hasData}>
						<Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={10}>
							<FaceliftText
								fontSize="md"
								fontWeight="semibold"
								_dark={{ color: 'gray.400' }}
								_light={{ color: 'gray.500' }}
							>
								No data found
							</FaceliftText>
						</Box>
					</Show>
					<Box height="calc(100% - 50px)" overflow={isFullScreen || disableScroll ? 'hidden' : 'auto'} pb={0}>
						<Box 
							width="100%" 
							height="100%" 
							minWidth={disableScroll ? '100%' : { base: '700px', md: '800px', lg: '900px' }} 
							display="flex"
							flexDirection="row"
							alignItems="center"
							justifyContent="center"
							gap={2}
							px={4}
							py={4}
						>
							{/* Chart Container */}
							<Box 
								flex="1" 
								height={chartHeight} 
								display="flex" 
								alignItems="center" 
								justifyContent="center"
								minHeight={chartHeight}
							>
								<ResponsiveContainer width="100%" height="100%">
									<PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
										<Show when={hasData}>
											<Pie
												data={visibleChartData}
												cx={chartCenterX}
												cy="50%"
												innerRadius="38%"
												outerRadius="72%"
												paddingAngle={0.5}
												dataKey="value"
												nameKey="name"
												label={renderCustomLabel}
												labelLine={false}
												isAnimationActive={true}
												animationBegin={0}
												animationDuration={800}
												animationEasing="ease-out"
												cornerRadius={4}
												stroke={isDark ? colors.darkModeBgColor : 'white'}
												strokeWidth={2}
												strokeLinejoin="round"
											>
												{visibleChartData.map((entry, index) => (
													<Cell key={`cell-${index}`} fill={entry.color} />
												))}
											</Pie>
										</Show>
										<Show when={hasData}>
											<Tooltip content={<CustomTooltip />} />
										</Show>
									</PieChart>
								</ResponsiveContainer>
							</Box>

							{/* Legend Container */}
							<Box
								width={isScrollRange ? 'auto' : { base: '100px', md: '110px', lg: '120px' }}
								minWidth={isScrollRange ? '200px' : '100px'}
								maxWidth={isScrollRange ? '350px' : 'none'}
								height={chartHeight}
								display="flex"
								alignItems="center"
								justifyContent="flex-start"
								flexShrink={0}
								overflowY="auto"
								pr={1}
							>
								<Stack direction="column" justify="center" align="flex-start" gap={2.5} width="100%">
									{chartData.map((item) => {
										const isDisabled = disabledSeries.has(item.name);
										// Show full names in scroll range (1000px-1200px), truncate elsewhere
										const displayName = isScrollRange 
											? item.name 
											: (item.name.length > 15 ? `${item.name.substring(0, 15)}...` : item.name);
										
										return (
											<Stack
												key={item.name}
												direction="row"
												align="center"
												gap={1.5}
												cursor="pointer"
												onClick={() => handleLegendClick(item.name)}
												opacity={isDisabled ? 0.4 : 1}
												transition="opacity 0.2s"
												_hover={{ opacity: isDisabled ? 0.6 : 0.8 }}
												width="100%"
											>
												<Box 
													width={2.5} 
													height={2.5} 
													minWidth={2.5}
													borderRadius="full" 
													bg={item.color} 
												/>
												<FaceliftText
													fontSize="2xs"
													fontWeight="medium"
													lineHeight="tight"
													_dark={{ color: 'gray.200' }}
													_light={{ color: 'gray.800' }}
													textDecoration={isDisabled ? 'line-through' : 'none'}
													title={item.name}
													overflow="hidden"
													textOverflow="ellipsis"
													whiteSpace="nowrap"
												>
													{displayName}
												</FaceliftText>
											</Stack>
										);
									})}
								</Stack>
							</Box>
						</Box>
					</Box>
				</Box>
			</Show>
			<FaceliftDialog
				title="Select Period"
				open={isDateFilterModalOpen}
				onOpenChange={(open) => {}}
				sx={{
					maxWidth: 400,
					maxHeight: 400,
				}}
				footer={
					<>
						<FaceliftButton
							width="100px"
							bg="transparent"
							color={{ _light: 'gray.600', _dark: 'gray.100' }}
							border="1px solid"
							borderColor={{ _light: 'gray.600', _dark: 'gray.100' }}
							onClick={() => {
								setIsDateFilterModalOpen(false);
							}}
						>
							Close
						</FaceliftButton>
						<FaceliftButton
							width="100px"
							onClick={() => {
								if (customDateRange.startDate && customDateRange.endDate) {
									// Convert DateRange format (startDate/endDate) to chart format (start/end)
									// DateRange uses MM/DD/YYYY format, convert to YYYY-MM-DD
									const parsedStartDate = dayjs(customDateRange.startDate, 'MM/DD/YYYY');
									const parsedEndDate = dayjs(customDateRange.endDate, 'MM/DD/YYYY');
									
									// Validate dates are valid before using
									if (parsedStartDate.isValid() && parsedEndDate.isValid()) {
										setDateRange({ 
											start: parsedStartDate.format('YYYY-MM-DD'), 
											end: parsedEndDate.format('YYYY-MM-DD') 
										});
										setIsDateFilterModalOpen(false);
									}
								}
							}}
							disabled={!customDateRange.startDate || !customDateRange.endDate}
						>
							Apply
						</FaceliftButton>
					</>
				}
			>
				<FaceliftDatePicker
					isRange={true}
					range={customDateRange}
					onRangeChange={setCustomDateRange}
					placeholder="Choose period..."
				/>
			</FaceliftDialog>
		</ChartWithTableView>
	);
};

export default PayorCollectionDonutChart;